// Change #100326 - edits MessagingChannel.BusinessHoursJson.
//
// Shape (WhatsAppLibrary::isWithinBusinessHours):
//   {"timezone":"Europe/London","days":{"1":[["09:00","17:30"]],...,"7":[]}}
// ISO day numbers, 1 = Monday. A null / empty config means "always open", which is what
// the "Always open" switch produces.
import React from "react";
import PropTypes from "prop-types";
import { Button, Input } from "reactstrap";

import { Switch } from "./shared";

export const DAYS = [
  ["1", "Monday"],
  ["2", "Tuesday"],
  ["3", "Wednesday"],
  ["4", "Thursday"],
  ["5", "Friday"],
  ["6", "Saturday"],
  ["7", "Sunday"],
];

const FALLBACK_ZONES = [
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome",
  "Europe/Amsterdam", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Australia/Sydney", "Pacific/Auckland", "Asia/Dubai", "UTC",
];

const timezones = (() => {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      const all = Intl.supportedValuesOf("timeZone");
      if (all && all.length) return all.includes("UTC") ? all : [...all, "UTC"];
    }
  } catch (_) {
    // older browsers - use the short list
  }
  return FALLBACK_ZONES;
})();

export const defaultBusinessHours = () => ({
  timezone: "Europe/London",
  days: {
    1: [["09:00", "17:30"]],
    2: [["09:00", "17:30"]],
    3: [["09:00", "17:30"]],
    4: [["09:00", "17:30"]],
    5: [["09:00", "17:30"]],
    6: [],
    7: [],
  },
});

/** Normalise whatever the API returned into the editor's shape, or null for "always open". */
export const normaliseBusinessHours = (cfg) => {
  if (!cfg || typeof cfg !== "object" || !cfg.days || typeof cfg.days !== "object") return null;
  const days = {};
  DAYS.forEach(([d]) => {
    const ranges = Array.isArray(cfg.days[d]) ? cfg.days[d] : [];
    days[d] = ranges
      .filter((r) => Array.isArray(r) && r.length === 2)
      .slice(0, 2)
      .map((r) => [String(r[0]), String(r[1])]);
  });
  return { timezone: cfg.timezone || "Europe/London", days };
};

/** Returns an error message, or null when the hours are usable. */
export const validateBusinessHours = (cfg) => {
  if (!cfg) return null;
  for (const [d, label] of DAYS) {
    const ranges = cfg.days[d] || [];
    for (const [start, end] of ranges) {
      if (!start || !end) return `${label}: enter a start and end time`;
      if (start >= end) return `${label}: the end time must be after the start time`;
    }
    if (ranges.length === 2 && ranges[1][0] < ranges[0][1]) {
      return `${label}: the second time range overlaps the first`;
    }
  }
  return null;
};

const BusinessHoursEditor = ({ value, onChange, disabled }) => {
  const alwaysOpen = !value;

  const setDay = (d, ranges) => onChange({ ...value, days: { ...value.days, [d]: ranges } });

  const setTime = (d, i, j, t) => {
    const ranges = value.days[d].map((r) => [...r]);
    ranges[i][j] = t;
    setDay(d, ranges);
  };

  return (
    <div className="wa-hours">
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        <Switch
          id="wa-hours-always"
          checked={alwaysOpen}
          disabled={disabled}
          label="Always open (no business hours)"
          onChange={(on) => onChange(on ? null : defaultBusinessHours())}
        />
        {!alwaysOpen && (
          <div className="d-flex align-items-center gap-2 ms-auto">
            <label className="form-label mb-0 small text-muted" htmlFor="wa-hours-tz">
              Timezone
            </label>
            <Input
              id="wa-hours-tz"
              type="select"
              bsSize="sm"
              className="wa-hours__tz"
              value={value.timezone}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, timezone: e.target.value })}
            >
              {(timezones.includes(value.timezone) ? timezones : [value.timezone, ...timezones]).map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </option>
              ))}
            </Input>
          </div>
        )}
      </div>

      {!alwaysOpen && (
        <div className="wa-hours__grid">
          {DAYS.map(([d, label]) => {
            const ranges = value.days[d] || [];
            const open = ranges.length > 0;
            return (
              <div key={d} className={`wa-hours__row${open ? "" : " is-closed"}`}>
                <div className="wa-hours__day">{label}</div>
                <Switch
                  id={`wa-hours-open-${d}`}
                  checked={open}
                  disabled={disabled}
                  label={open ? "Open" : "Closed"}
                  onChange={(on) => setDay(d, on ? [["09:00", "17:30"]] : [])}
                />
                <div className="wa-hours__ranges">
                  {ranges.map((r, i) => (
                    <div key={i} className="wa-hours__range">
                      <Input
                        type="time"
                        bsSize="sm"
                        value={r[0]}
                        disabled={disabled}
                        aria-label={`${label} start ${i + 1}`}
                        onChange={(e) => setTime(d, i, 0, e.target.value)}
                      />
                      <span className="text-muted">to</span>
                      <Input
                        type="time"
                        bsSize="sm"
                        value={r[1]}
                        disabled={disabled}
                        aria-label={`${label} end ${i + 1}`}
                        onChange={(e) => setTime(d, i, 1, e.target.value)}
                      />
                      {i === 1 && (
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 text-danger"
                          disabled={disabled}
                          title="Remove the second time range"
                          onClick={() => setDay(d, ranges.slice(0, 1))}
                        >
                          <i className="ri-close-line" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {ranges.length === 1 && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      disabled={disabled}
                      // Split the day around lunch - the times are editable straight after.
                      onClick={() => setDay(d, [[ranges[0][0], "12:30"], ["13:30", ranges[0][1]]])}
                    >
                      <i className="ri-add-line me-1" />
                      Split shift
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

BusinessHoursEditor.propTypes = {
  value: PropTypes.shape({
    timezone: PropTypes.string,
    days: PropTypes.object,
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default BusinessHoursEditor;
